import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {BiomatchResponse, CompareBiomatchResponse, FieldsErrorResponse} from '@chekin/api';
import useBuildOCRDetectedData from '../useBuildOCRDetectedData';
import {PATHS} from 'routes/constants';
import {STORAGE_KEYS} from '../../utils/constants';
import {
  Guest,
  GUEST_STATUSES,
  IMAGE_SERVICE_ORIGIN,
  IV_DOCUMENT_IMAGE_TYPES,
  OCRDetectedData,
  SDK_MODE,
  SessionStorage,
} from '@chekin/core';
import {
  StoredImageDetails,
  omitRawImage,
  useIVStore,
  getDocumentNationality,
  getDocumentType,
  useS3Upload,
  useUpdateIvIntent,
  ANALYTIC_EVENTS,
  Analytics,
  useBiomatchFlow,
  usePhotoBoxStore,
  useIVCompletionFlow,
  useIVProcessState,
  useLivenessVideoUpload,
  useOCRFlow,
  useErrorModal,
} from '@chekin/shared';
import {useGetGuestId} from '../useGetGuestId';
import {useSummary} from 'context/summary';
import {usePath} from '../common';
import {useIVComputedDetails} from '../useIVComputedDetails';
import {useGetNextStepAfterIV} from '../useGetNextStepAfterIV';
import {useVerificationCompleted} from 'components/VerificationCompletedModal';
import {useKioskoScannerControls} from 'context/kiosko/hooks/useKioskoScannerControls';
import {useEvent} from '../useEvent';
import {useGuestMutation} from '../useGuestMutation';
import {useBuildIVPayload} from '../useBuildIVPayload';
import {useGuest} from '../useGuest';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {getIVIntentRemoteSession} from './useIVRemoteSync';
import ChekinProSettings from '../../ChekinProSettings';
import {useLockScrollBody} from '@chekin/guestapp-ui-kit';
import {useIVModalComponents} from './documentSelfieDetection/useIVModalComponents';

type UseUpdateGuestIVData = {
  onSuccess: (data: Guest) => void;
  onError: (error: FieldsErrorResponse) => void;
};

function useUpdateGuestIVData(props: UseUpdateGuestIVData) {
  const onSuccess = useEvent(props.onSuccess);
  const onError = useEvent(props.onError);
  const mutate = useGuestMutation({
    onSuccess,
    onError,
    isPatch: true,
  });
  const {getIVPayload} = useBuildIVPayload();

  const updateGuest = useEvent((id: string) => {
    const payload = getIVPayload();
    mutate.mutate({
      id,
      payload,
    });
  });

  return {updateGuest};
}


type UseDocumentSelfieDetection = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  onlyScanning?: boolean;
  onError?: (error: Error | string) => void;
  livenessVideo?: Blob | null;
};

function useDocumentSelfieDetection({
  onError,
  onlyScanning,
  docType,
  livenessVideo,
}: UseDocumentSelfieDetection = {}) {
  const {sendUpdateIvIntentRequestAsync} = useUpdateIvIntent();
  const {openModal: openIVCompletedModal} = useVerificationCompleted();
  const navigate = useNavigate();
  const resetPhotoBoxUploadedImage = usePhotoBoxStore(state => state.resetUploadedImage);
  const {
    setDistanceBiomatchComparing,
    setOCRDocumentPassed,
    resetIVConfigState,
    setBiomatchPassed,
    setImageDetails,
    isRemoteSession,
    imagesDetails,
  } = useIVStore();
  const {t} = useTranslation();
  const id = useGetGuestId();
  const {guest} = useGuest({id});
  const needToUpdateGuest =
    id && guest?.guestapp_status === GUEST_STATUSES.verification_pending;
  const {biomatch, required} = useIVComputedDetails();
  const {reservationId, countryCode} = useSummary();
  const {buildFormData} = useBuildOCRDetectedData();
  const documentType = useIVStore(getDocumentType);
  const nationality = useIVStore(getDocumentNationality);
  const frontSideImage = useIVStore(state => state.imagesDetails.front);
  const livenessResultData = useIVStore(state => state.livenessResultData);
  const setLivenessResultData = useIVStore(state => state.setLivenessResultData);
  const isIvFailed = useIVStore(state => state.isIvFailed);
  const {getMainPath} = usePath();
  const {resetScroll} = useLockScrollBody();
  const layout = useLayoutMode();
  const {ErrorModal: DefaultErrorModal, closeErrorModal, displayError} = useErrorModal();
  const {isScanning: isKioskoScanning} = useKioskoScannerControls();
  const {getNextStepDetails} = useGetNextStepAfterIV({docType, onlyScanning});
  const {uploadFile} = useS3Upload();
  const isBiomatchAndPassport = biomatch && !documentType?.two_sides;

  const processStateController = useIVProcessState({
    isKioskoScanning,
    displayError,
    onError,
    resetScroll,
    onCancelRetry: () => {
      resetPhotoBoxUploadedImage();
    },
  });

  const shouldOpenCompletionModal = useCallback(
    (path?: string) => {
      if (!isRemoteSession || !path) {
        return false;
      }

      return (
        path.endsWith(PATHS.addPersonalDataForm) ||
        path.endsWith(PATHS.guestRegistration)
      );
    },
    [isRemoteSession],
  );

  const {handleFinish} = useIVCompletionFlow({
    getNextStepDetails,
    setProcessState: processStateController.setProcessState,
    navigateTo: navigate,
    onAllStepsCompleted: () => {
      Analytics.track(ANALYTIC_EVENTS.ivFinishingAllSuccessfully);
    },
    onAfterSuccessDelay: (nextStep, postHandler) => {
      const shouldShowCompletionModal = shouldOpenCompletionModal(nextStep.path);

      if (ChekinProSettings.isSDK && nextStep.completed) {
        const state = useIVStore.getState();
        ChekinProSettings.events.onIVFinished({
          imagesDetails: state.imagesDetails,
          ...state.ocrResultData,
          ...state.biomatchResultData,
        });

        if (ChekinProSettings.mode === SDK_MODE.onlyIV) {
          return true;
        }
      }

      if (shouldShowCompletionModal) {
        openIVCompletedModal(postHandler);
        return true;
      }

      return false;
    },
  });

  const {updateGuest} = useUpdateGuestIVData({
    onError: processStateController.handleError,
    onSuccess: data => {
      if (data.guestapp_status === GUEST_STATUSES.complete) {
        SessionStorage.remove(STORAGE_KEYS.personalFormData);
        resetIVConfigState();
        handleFinish(getMainPath(PATHS.guestRegistration));
        return;
      }

      handleFinish();
    },
  });

  const finishFlow = useCallback(() => {
    if (needToUpdateGuest) {
      setTimeout(() => updateGuest(id), 0);
      return;
    }

    handleFinish();
  }, [handleFinish, id, needToUpdateGuest, updateGuest]);

  const saveImageDetails = useCallback(
    async (nextDocType: IV_DOCUMENT_IMAGE_TYPES, details: StoredImageDetails) => {
      setImageDetails(details);
      const intentId = getIVIntentRemoteSession();
      if (!intentId) return;

      await sendUpdateIvIntentRequestAsync({
        id: intentId,
        image_details: {
          ...imagesDetails,
          [nextDocType]: omitRawImage(details),
        },
      });
    },
    [imagesDetails, sendUpdateIvIntentRequestAsync, setImageDetails],
  );

  const saveOCRData = useCallback(
    async (data: OCRDetectedData) => {
      if (!onlyScanning) {
        const intentId = getIVIntentRemoteSession();
        if (intentId) {
          await sendUpdateIvIntentRequestAsync({
            id: intentId,
            document_passed: true,
          });
        }

        setOCRDocumentPassed(data?.mrz_detected || data?.barcode_detected);
      }

      SessionStorage.set(STORAGE_KEYS.personalFormData, buildFormData(data));
    },
    [buildFormData, onlyScanning, sendUpdateIvIntentRequestAsync, setOCRDocumentPassed],
  );

  const handleLivenessUploadFinished = useCallback(
    (value: string) => {
      setLivenessResultData({
        ...livenessResultData,
        livenessVideoUrl: value,
      });
    },
    [livenessResultData, setLivenessResultData],
  );

  const {uploadLivenessVideo} = useLivenessVideoUpload({
    uploadFile,
    onUploadFinished: handleLivenessUploadFinished,
  });

  const saveComparisonSuccess = useCallback(
    async (data: CompareBiomatchResponse) => {
      const intentId = getIVIntentRemoteSession();

      setBiomatchPassed(data.is_match);
      setDistanceBiomatchComparing(Number(data.distance?.toFixed(1) ?? 0.0));

      if (livenessVideo) {
        const videoUrl = await uploadLivenessVideo(livenessVideo);
        if (intentId && livenessResultData) {
          await sendUpdateIvIntentRequestAsync({
            id: intentId,
            biomatch_passed: true,
            liveness_data: {
              passed: livenessResultData.passed ?? false,
              completed_actions: livenessResultData.completedActions,
              video_url: videoUrl,
              performed_by: livenessResultData.performedBy,
            },
            is_iv_failed: isIvFailed,
          });
        }
        return;
      }

      if (intentId) {
        await sendUpdateIvIntentRequestAsync({
          id: intentId,
          biomatch_passed: true,
        });
      }
    },
    [
      isIvFailed,
      livenessResultData,
      livenessVideo,
      sendUpdateIvIntentRequestAsync,
      setBiomatchPassed,
      setDistanceBiomatchComparing,
      uploadLivenessVideo,
    ],
  );

  const handleBiomatchRetriesFinish = useCallback(
    (data: CompareBiomatchResponse | BiomatchResponse) => {
      Analytics.track(ANALYTIC_EVENTS.ivRetryingLimitOver, {
        check_id: data.id,
        status: data.status,
        picture_file: (data as BiomatchResponse).picture_file,
        is_match: Boolean((data as CompareBiomatchResponse).is_match),
        type: 'Biomatch',
      });
      processStateController.openRetryModal();
    },
    [processStateController],
  );

  const handleOCRRetriesFinish = useCallback(
    (data: OCRDetectedData) => {
      Analytics.track(ANALYTIC_EVENTS.ivRetryingLimitOver, {
        check_id: data.id,
        status: data.status,
        picture_file: data.picture_file,
        mrz_detected: Boolean(data.mrz_detected),
        barcode_detected: Boolean(data.barcode_detected),
        type: 'OCR',
      });
      processStateController.openRetryModal();
    },
    [processStateController],
  );

  const handleFailed = useCallback(
    (nextErrorMessage?: string) => {
      if (docType === IV_DOCUMENT_IMAGE_TYPES.front) {
        setImageDetails({type: docType, id: '', service: IMAGE_SERVICE_ORIGIN.biomatch});
      }

      if (docType === IV_DOCUMENT_IMAGE_TYPES.back) {
        setImageDetails({type: docType, id: ''});
      }

      Analytics.track(ANALYTIC_EVENTS.ivError, {
        'Error message': nextErrorMessage ?? '',
        'Image type': docType,
      });
      processStateController.setFailureState(nextErrorMessage);
    },
    [docType, processStateController, setImageDetails],
  );

  const ocrFlow = useOCRFlow({
    context: {
      docType,
      onlyScanning,
      isBiomatchAndPassport,
      documentType,
      frontSideImage,
      countryCode,
      reservationId,
    },
    actions: {
      saveOCRData,
      saveOCRImageDetails: saveImageDetails,
      finishFlow,
    },
    handlers: {
      onError: processStateController.handleError,
      onBefore: processStateController.onBefore,
      onRetryLimitReached: handleOCRRetriesFinish,
      onFailed: handleFailed,
    },
  });

  const biomatchFlow = useBiomatchFlow({
    context: {
      docType,
      documentType,
      frontSideImage,
      reservationId,
      nationality,
      isBiomatchAndPassport,
    },
    actions: {
      saveBiomatchImageDetails: saveImageDetails,
      saveComparisonSuccess,
      runOCR: ocrFlow.sendAndVerifyImageForOCR,
      finishFlow,
    },
    handlers: {
      onError: processStateController.handleError,
      onBefore: processStateController.onBefore,
      onRetryLimitReached: handleBiomatchRetriesFinish,
      onFailed: handleFailed,
    },
  });

  const continueRegistration = useCallback(() => {
    navigate(getMainPath(PATHS.addPersonalDataForm));
  }, [getMainPath, navigate]);

  const {ProcessModal, RetryModal, ErrorModal} = useIVModalComponents({
    docType,
    layout,
    required,
    t,
    errorMessage: processStateController.errorMessage,
    processState: processStateController.processState,
    isProcessModalOpen: processStateController.isProcessModalOpen,
    closeAndCleanLoadingModal: processStateController.closeAndCleanLoadingModal,
    isRetryModalOpen: processStateController.isRetryModalOpen,
    handleRetryAgain: processStateController.handleRetryAgain,
    handleCancelRetryAgain: processStateController.handleCancelRetryAgain,
    DefaultErrorModal,
    closeErrorModal,
    continueRegistration,
  });

  const sendAndReadImageForOCRWrapped = processStateController.wrapRetryable(
    ocrFlow.sendAndVerifyImageForOCR,
  );
  const sendSelfieToDetectFaceWrapped = processStateController.wrapRetryable(
    biomatchFlow.sendAndVerifySelfieForFaceDetection,
  );
  const sendSelfieAndDocumentToCompareWrapped = processStateController.wrapRetryable(
    biomatchFlow.sendSelfieAndDocumentToCompare,
  );
  const sendFrontSideDocumentWrapped = processStateController.wrapRetryable(
    biomatch || (!biomatch && documentType?.two_sides)
      ? biomatchFlow.sendAndVerifyDocumentToBiomatchAndOCR
      : ocrFlow.sendAndVerifyImageForOCR,
  );
  const sendBackSideDocumentWrapped = processStateController.wrapRetryable(
    ocrFlow.sendAndVerifyImageForOCR,
  );

  return {
    sendSelfieAndDocumentToCompare: sendSelfieAndDocumentToCompareWrapped,
    sendSelfieToDetectFace: sendSelfieToDetectFaceWrapped,
    sendAndReadImageForOCR: sendAndReadImageForOCRWrapped,
    sendFrontSideDocument: sendFrontSideDocumentWrapped,
    sendBackSideDocument: sendBackSideDocumentWrapped,
    isProcessing: processStateController.isProcessModalOpen,
    processState: processStateController.processState,
    ProcessModal,
    ErrorModal,
    RetryModal,
  };
}

export {useDocumentSelfieDetection};
